// CoverageChecker.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../navbar/Navbar";
import Footer from "../footer/Footer";
import LoaderButton from "../button/LoaderButton";
import CustomSelect from "../common/dropdown/CustomSelect";
import { MdLocationOn } from "react-icons/md";
import AddressService from "../../services/AddressService";
import ModalComponent from "../modalComponent/ModalComponent";
import { useDispatch, useSelector } from "react-redux";
import {
  closeModal,
  openModal,
  selectModalState,
} from "../modalComponent/modalSlice";
import Button from "../button/Button";
import globe from "../../assets/custom-icons/Globe.svg";

const ModalForCoverageCheckWarning = ({ navigate, dispatch }) => {
  return (
    <div>
      <h2 className="text-md font-semibold mb-2">
        Sorry, we’re not in your area yet. Stay tuned for updates.
      </h2>
      <div className="w-full flex justify-center items-center mt-4">
        <Button
          handleClick={() => {
            dispatch(closeModal());
            navigate("/");
          }}
          title="Okay"
          type="button"
          style="cta-two"
        />
      </div>
    </div>
  );
};

const ModalForSuccess = ({ dispatch }) => {
  const navigate = useNavigate();

  const handleOkayClick = () => {
    dispatch(closeModal());
    navigate("/hbbDevices");
  };

  return (
    <div className="relative">
      <div className="flex justify-between items-start mb-2">
        <h2 className="text-md font-semibold">
          Congratulations, we can install HBB in your area. Please click{" "}
          <b>Continue</b> to proceed.
        </h2>
      </div>
      <div className="w-full flex justify-center items-center mt-4">
        <Button
          handleClick={handleOkayClick}
          title="Continue"
          type="button"
          style="cta-two"
        />
      </div>
    </div>
  );
};

const CoverageChecker = () => {
  const modalInitState = useSelector((state) => state.modal);
  const [postcode, setPostcode] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [postcodeMatchError, setPostcodeMatchError] = useState("");
  const [coverageLoading, setCoverageLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Assume current database postcode is stored in localStorage or a constant (optional, for reference)
  const currentDatabasePostcode =
    localStorage.getItem("currentPostcode") || "W1A 4ZX"; // Example value

  const handlePostcodeSearch = () => {
    setSelectedAddress(null);
    setAddresses([]);
    setError("");
    setPostcodeMatchError("");

    if (postcode && postcode.trim().length > 0) {
      const zipCodeRegex = /^[a-zA-Z0-9\s]{6,8}$/;
      if (!zipCodeRegex.test(postcode)) {
        setError("Invalid Post Code format");
        return;
      }

      setLoading(true);
      AddressService.addressApi(postcode)
        .then((response) => {
          if (response.status === 200) {
            const data = response.data;
            if (data && data.length > 0) {
              setAddresses(data);
              setDropdownVisible(true);
            } else {
              setError("No addresses found for this postcode");
            }
          }
        })
        .catch(() => {
          setError("Failed to fetch addresses for the entered postcode.");
        })
        .finally(() => setLoading(false));
    } else {
      setError("Please enter post code");
    }
  };

  const handleAddressSelect = (addressStr) => {
    setSelectedAddress(addressStr);
    setPostcodeMatchError("");
    setCoverageLoading(true);

    // Find the selected address object to get its postcode
    const selectedAddr = addresses.find(
      (addr) => addr.addressStr === addressStr
    );
    if (selectedAddr) {
      const selectedPostcode = selectedAddr.postCode;
      // alert(selectedPostcode);
      // Call checkCoverage API with the selected postcode
      AddressService.checkCoverage(selectedPostcode)
        .then((response) => {
          // alert(JSON.stringify(response));
          if (response.status === 200) {
            dispatch(openModal("coverageCheckSuccess"));
          }
        })
        .catch((error) => {
          if (error.response) {
            const status = error.response.status;
            switch (status) {
              case 406:
                dispatch(openModal("coverageCheckWarning"));
                break;
              case 400:
                setError("Bad request. Please check the postcode format.");
                break;
              case 404:
                setError("Postcode not found in our records.");
                break;
              case 500:
                setError("Server error. Please try again later.");
                break;
              default:
                setError("An unexpected error occurred. Please try again.");
            }
          } else if (error.message === "Network Error") {
            setError("Network error. Please check your connection.");
          } else {
            setError("An unexpected error occurred. Please try again.");
          }
        })
        .finally(() => setLoading(false));
    } else {
      setError("Please enter post code");
    }
  };

  const handleSubmit = () => {
    navigate("/hbbDevices"); // Replace with your submit logic
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="mx-auto max-w-3xl w-full px-4 sm:px-6 lg:px-8 mt-5">
        <div className="card-coverage relative rounded-[1.5rem] bg-[#5A949A] p-[0.1rem]">
          <h4 className="text-xl text-white ml-6 mt-6 font-semibold">
            Check Coverage
          </h4>
          <img
            className="absolute top-0 right-0 mt-2 mr-[-2rem] "
            src={globe}
            alt=""
          />
          <div className="mt-[1.5rem] bg-[#fff] rounded-[1.5rem] py-6 px-6">
            <div className="space-y-4">
              <h3 className="text-md font-semibold text-[#334]">
                Find your address
              </h3>
              <div className="space-y-3">
                <input
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  placeholder="Postal Code"
                  className="w-full p-3 border border-gray-300 text-gray-700 rounded-lg focus:ring-2 focus:ring-[#5A949A] focus:border-[#5A949A] transition-all duration-200"
                />
                {error && (
                  <p className="error-text" role="alert">
                    {error}
                  </p>
                )}

                {addresses.length > 0 && dropdownVisible && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <label className="text-md font-semibold text-[#334]">
                      Suggested Addresses
                    </label>
                    <CustomSelect
                      value={selectedAddress}
                      options={addresses.map((addr) => addr.addressStr)}
                      onChange={handleAddressSelect}
                      icon={
                        <MdLocationOn className="text-lg opacity-40 mr-1 text-[#5A949A]" />
                      }
                    />
                    {postcodeMatchError && (
                      <p className="error-text">{postcodeMatchError}</p>
                    )}
                  </div>
                )}

                {selectedAddress && !postcodeMatchError && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-md font-semibold text-[#334]">
                      Selected Address
                    </p>
                    <div className="text-[#5A949A] text-sm space-y-1">
                      {selectedAddress.split(",").map((line, idx) => (
                        <div key={idx} className="flex items-start">
                          <span className="text-[#5A949A] mr-2">•</span>
                          <span>{line.trim()}</span>
                        </div>
                      ))}
                    </div>
                    {/* <div className="flex justify-center mt-4">
                      <LoaderButton
                        title="Submit"
                        type="button"
                        handleClick={handleSubmit}
                        className="w-full rounded-lg bg-[#5A949A] hover:bg-[#4a7f85] text-white"
                      />
                    </div> */}
                  </div>
                )}

                <div className="w-full">
                  <LoaderButton
                    title="Search Availability"
                    handleClick={handlePostcodeSearch}
                    disabled={loading}
                    className="w-full rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <ModalComponent
        size={
          modalInitState.contentId === "coverageCheckWarning"
            ? "md"
            : modalInitState.contentId === "coverageCheckSuccess"
            ? "md"
            : null
        }
        type={
          modalInitState.contentId === "coverageCheckWarning"
            ? "alert"
            : modalInitState.contentId === "coverageCheckSuccess"
            ? "success"
            : null
        }
        position={
          modalInitState.contentId === "coverageCheckWarning" ||
          modalInitState.contentId === "coverageCheckSuccess"
            ? "center"
            : null
        }
        title={
          modalInitState.contentId === "coverageCheckWarning"
            ? "Coverage Check Warning"
            : modalInitState.contentId === "coverageCheckSuccess"
            ? "Coverage Check Success"
            : null
        }
        description=""
        bottomLine={true}
        crossButton={
          modalInitState.contentId === "coverageCheckWarning" ||
          modalInitState.contentId === "coverageCheckSuccess"
            ? true
            : false
        }
        style={{
          maxWidth: "500px",
          maxHeight: "300px",
          margin: "auto",
        }}
      >
        {modalInitState.contentId === "coverageCheckWarning" ? (
          <ModalForCoverageCheckWarning
            navigate={navigate}
            dispatch={dispatch}
          />
        ) : modalInitState.contentId === "coverageCheckSuccess" ? (
          <ModalForSuccess dispatch={dispatch} />
        ) : null}
      </ModalComponent>
      <Footer />
    </div>
  );
};

export default CoverageChecker;
